import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, HelpCircle, Search, ShieldCheck, Clock, Truck, IndianRupee } from "lucide-react";

const CATEGORIES = [
  {
    id: "basics",
    icon: HelpCircle,
    label: "PUC Basics",
    faqs: [
      { q: "What is a PUC (or PUCC) certificate?", a: "PUC stands for Pollution Under Control. A PUCC (Pollution Under Control Certificate) is a document issued after a vehicle passes an emissions test at an RTO-authorised centre, certifying that the vehicle's exhaust emissions are within the limits prescribed by the Central Motor Vehicles Rules, 1989." },
      { q: "Is PUC mandatory in India?", a: "Yes. Under Section 190(2) of the Motor Vehicles Act, every motor vehicle running on Indian roads — petrol, diesel, CNG, LPG or hybrid — must carry a valid PUC certificate at all times. EVs are currently exempt as they have zero tailpipe emissions." },
      { q: "How long is a PUC certificate valid?", a: "For a new vehicle, the PUC is valid for 1 year from the date of first registration. After that, it must be renewed every 6 months. Some states may have shorter validities for older or high-emission commercial vehicles — the machine-printed expiry date on your certificate is always the source of truth." },
      { q: "Which vehicles need a PUC?", a: "All BS-II, BS-III, BS-IV and BS-VI vehicles: two-wheelers, three-wheelers, four-wheelers (cars, taxis, SUVs) and commercial vehicles (trucks, buses, tempos, school buses, cabs). Battery-electric vehicles are exempt." },
    ],
  },
  {
    id: "penalty",
    icon: ShieldCheck,
    label: "Penalties & Legal",
    faqs: [
      { q: "What is the penalty for driving without a valid PUC?", a: "Under Section 190(2) of the Motor Vehicles (Amendment) Act 2019, the penalty is a fine of ₹10,000 or imprisonment up to 3 months, or both. A second offence can lead to a fine of ₹10,000 and/or 6 months imprisonment. In some states you may also get community service." },
      { q: "Can the traffic police impound my vehicle for no PUC?", a: "Traffic police cannot impound your vehicle solely for a missing PUC in most states — but they can issue an on-the-spot challan, and the vehicle may be temporarily seized if the certificate is expired and you refuse to comply." },
      { q: "Is PUC required for a new vehicle straight from the showroom?", a: "New vehicles come with a 1-year PUC issued by the dealer at delivery. You do not need a fresh test for the first year, but you must carry the dealer-issued certificate." },
      { q: "Do I need a PUC to renew insurance?", a: "Yes. Since 2018, insurers are required by IRDAI to verify a valid PUC before renewing your motor insurance policy. Without a valid PUC, insurance renewal is rejected." },
    ],
  },
  {
    id: "test",
    icon: Clock,
    label: "Test process",
    faqs: [
      { q: "How long does a PUC test take?", a: "A typical PUC test at a certified centre takes 5–10 minutes for two-wheelers and 10–15 minutes for cars. Commercial diesel vehicles take slightly longer (up to 20 minutes) due to the smoke opacity test." },
      { q: "What documents do I need to carry?", a: "Just the vehicle's RC (Registration Certificate) — physical or digital via DigiLocker. Some centres also ask for the previous PUC. You do not need to carry ID." },
      { q: "What is measured during the test?", a: "For petrol/CNG/LPG vehicles: Carbon Monoxide (CO) and Hydrocarbons (HC) via a gas analyser. For diesel vehicles: smoke density in HSU (Hartridge Smoke Units) using a smoke meter. The centre uploads the reading to the Parivahan database in real time." },
      { q: "What if my vehicle fails the PUC test?", a: "You will not be issued a certificate. Common causes: faulty spark plugs, dirty air filter, worn catalytic converter, incorrect fuel-air mixture, or engine tuning. Fix the underlying issue, then retest — most centres allow a free or discounted retest within 24-48 hours." },
      { q: "Is the certificate digital?", a: "Yes. A machine-generated PUC with a QR code is printed and simultaneously uploaded to the central Vahan/Parivahan database. Digital copies via DigiLocker or the mParivahan app are legally equivalent to a printed one." },
    ],
  },
  {
    id: "booking",
    icon: Truck,
    label: "VayuCheck booking",
    faqs: [
      { q: "How does booking a PUC test on VayuCheck work?", a: "Choose a certified centre near you, pick a time slot, enter your vehicle details, and confirm. You'll get an SMS + email confirmation. Walk in at your slot, get tested, and drive out with the certificate on the spot." },
      { q: "Do I have to pay online?", a: "Not at the moment. Payment is made directly at the centre after the test. VayuCheck does not charge you a platform fee." },
      { q: "Can I book for multiple vehicles at once?", a: "Yes — commercial and fleet accounts can bulk-add their entire fleet and book each vehicle in one click from the dashboard. Individual accounts can save vehicles and use the 'Book now' button for one-tap renewals." },
      { q: "Will I be reminded before my PUC expires?", a: "Yes. Once a partner marks your booking as completed with the certificate validity date, VayuCheck automatically emails and SMSes you 3 days before it expires — no more forgetting until you're stopped at a check-post." },
      { q: "Can I cancel or reschedule a booking?", a: "Yes. Log in to your dashboard, find the booking under 'My PUC Bookings', and update the status. Rescheduling is subject to the partner centre's available slots." },
    ],
  },
  {
    id: "cost",
    icon: IndianRupee,
    label: "Cost & pricing",
    faqs: [
      { q: "How much does a PUC test cost?", a: "PUC test fees are regulated by state RTOs and generally range from ₹60 to ₹100 for two-wheelers, ₹80 to ₹150 for petrol cars, and ₹100 to ₹250 for diesel and commercial vehicles. The centre's rate card is displayed on their listing page." },
      { q: "Does VayuCheck charge extra?", a: "No. You pay exactly what the RTO-approved partner charges at the centre. VayuCheck is free for customers." },
      { q: "Are there discounts for fleets?", a: "Some partner centres offer volume discounts for commercial accounts booking 10+ vehicles at once. These are displayed on the centre's page and negotiated directly." },
    ],
  },
];

export default function Faqs() {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CATEGORIES;
    return CATEGORIES
      .map((c) => ({ ...c, faqs: c.faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) }))
      .filter((c) => c.faqs.length > 0);
  }, [query]);

  const totalCount = CATEGORIES.reduce((n, c) => n + c.faqs.length, 0);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="bg-mesh border-b border-border relative overflow-hidden">
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-primary/15 blur-3xl animate-drift" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-6 py-20 md:py-24">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1.5 rounded-full mb-6">
              <HelpCircle className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">FAQs · PUCC</span>
            </div>
            <h1 data-testid="faqs-h1" className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-[1]">
              Everything about your <span className="bg-gradient-to-r from-primary via-cyan-500 to-emerald-500 bg-clip-text text-transparent gradient-animated">PUC certificate</span>.
            </h1>
            <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">
              {totalCount} answers on the Pollution Under Control Certificate — legality, testing, penalties, costs, and how VayuCheck fits in.
            </p>

            <div className="relative mt-10 max-w-xl">
              <Search className="w-4 h-4 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="faqs-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs — try 'penalty', 'diesel', 'validity'…"
                className="rounded-full bg-white border-border h-12 pl-11 pr-4 shadow-sm"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-4xl px-6 py-16 space-y-16" data-testid="faqs-body">
          {filtered.length === 0 && (
            <div className="border border-dashed border-border rounded-2xl p-16 text-center" data-testid="faqs-empty">
              <h3 className="font-display text-2xl font-bold mb-2">No matching FAQs</h3>
              <p className="text-muted-foreground">Try a different search term or browse categories below.</p>
            </div>
          )}
          {filtered.map((cat) => (
            <div key={cat.id} data-testid={`faq-category-${cat.id}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><cat.icon className="w-5 h-5 text-primary" /></div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">{cat.faqs.length} answer{cat.faqs.length !== 1 ? "s" : ""}</div>
                  <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{cat.label}</h2>
                </div>
              </div>
              <Accordion type="single" collapsible className="border border-border rounded-2xl divide-y divide-border overflow-hidden bg-white">
                {cat.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`${cat.id}-${i}`} className="border-none px-6" data-testid={`faq-${cat.id}-${i}`}>
                    <AccordionTrigger className="text-left font-display text-base md:text-lg font-semibold hover:no-underline py-5">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pr-6 text-sm md:text-base">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-border bg-mesh-soft">
        <div className="mx-auto max-w-4xl px-6 py-20 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">Still have questions?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">Book a test to talk to a certified operator, or reach out to us directly.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/book" data-testid="faqs-book-cta"><Button size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-semibold shadow-lg shadow-primary/25">Book PUC test <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <a href="mailto:support@vayucheck.in"><Button size="lg" variant="outline" className="rounded-full border-border text-foreground hover:bg-secondary h-12 px-8">Email support</Button></a>
          </div>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <div>© {new Date().getFullYear()} VayuCheck</div>
        <div className="flex gap-6 flex-wrap">
          <Link to="/about" className="hover:text-foreground">About</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
